import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { BedDouble, Maximize, MapPin, X, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const LOCATIONS = ["Cannes", "Antibes", "Nice", "Mougins", "Juan-les-Pins", "Saint-Raphaël", "Saint-Jean-Cap-Ferrat"];
const selectCls =
  "rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-light text-azur outline-none transition-colors focus:border-azur";

export default function Biens() {
  const [searchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [type, setType] = useState(searchParams.get("type") || "");
  const [location, setLocation] = useState(searchParams.get("location") || "");
  const [budget, setBudget] = useState(
    searchParams.get("min_budget") ? `min${searchParams.get("min_budget")}` : searchParams.get("max_budget") || ""
  );
  const [tag, setTag] = useState("");
  const navigate = useNavigate();
  const [focus, setFocus] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => setImgIdx(0), [focus]);

  const gallery = useMemo(() => {
    if (!focus) return [];
    return [focus.image, ...(focus.gallery || []).filter((g) => g && g !== focus.image)];
  }, [focus]);

  useEffect(() => {
    const params = {};
    if (type) params.type = type;
    if (location) params.location = location;
    if (budget) {
      if (budget.startsWith("min")) params.min_budget = budget.slice(3);
      else params.max_budget = budget;
    }
    if (tag) params.tag = tag;
    api.get("/properties", { params }).then((r) => setProperties(r.data)).catch(() => {});
  }, [type, location, budget, tag]);

  useEffect(() => {
    const focusId = searchParams.get("focus");
    if (focusId) {
      api.get(`/properties/${focusId}`).then((r) => setFocus(r.data)).catch(() => {});
    }
  }, [searchParams]);

  const hasFilters = useMemo(() => type || location || budget || tag, [type, location, budget, tag]);

  const reset = () => {
    setType("");
    setLocation("");
    setBudget("");
    setTag("");
  };

  const sendLead = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", {
        kind: "contact",
        ...lead,
        details: { bien: focus.title, reference: focus.id },
      });
      toast.success("Votre demande a bien été envoyée. Nous revenons vers vous très vite.");
      setLead({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Une erreur est survenue, veuillez réessayer.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Nos propriétés"
        title="Des adresses sélectionnées avec soin sur la Côte d'Azur."
        sub="Chaque bien de notre portfolio a été visité, estimé et choisi. Explorez notre sélection."
      />

      <section data-testid="biens-page" className="mx-auto max-w-7xl px-4 py-14 sm:px-8 lg:px-16">
        <Reveal className="flex flex-wrap items-center gap-3">
          <select data-testid="biens-filter-type" value={type} onChange={(e) => setType(e.target.value)} className={selectCls}>
            <option value="">Tous types</option>
            <option value="Villa">Villas</option>
            <option value="Appartement">Appartements</option>
            <option value="Maison">Maisons</option>
            <option value="Local commercial">Locaux commerciaux</option>
          </select>
          <select data-testid="biens-filter-location" value={location} onChange={(e) => setLocation(e.target.value)} className={selectCls}>
            <option value="">Toutes localisations</option>
            {LOCATIONS.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
          <select data-testid="biens-filter-budget" value={budget} onChange={(e) => setBudget(e.target.value)} className={selectCls}>
            <option value="">Budget max</option>
            {Array.from({ length: 10 }, (_, i) => (i + 1) * 100000).map((v) => (
              <option key={v} value={v}>≤ {v.toLocaleString("fr-FR")} €</option>
            ))}
            <option value="min1000000">Plus de 1 000 000 €</option>
          </select>
          <select data-testid="biens-filter-tag" value={tag} onChange={(e) => setTag(e.target.value)} className={selectCls}>
            <option value="">Tous critères</option>
            <option value="Exclusivité">Exclusivités</option>
            <option value="Vue mer">Vue mer</option>
            <option value="Investissement">Investissement</option>
          </select>
          {hasFilters && (
            <button
              onClick={reset}
              data-testid="biens-filter-reset"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2.5 text-xs font-medium text-slate-600 transition-colors hover:border-terracotta hover:text-terracotta"
            >
              <X size={13} /> Réinitialiser
            </button>
          )}
        </Reveal>

        <p data-testid="biens-count" className="mt-8 font-mono text-xs uppercase tracking-[0.25em] text-slate-400">
          {properties.length} bien{properties.length > 1 ? "s" : ""} disponible{properties.length > 1 ? "s" : ""}
        </p>

        <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {properties.map((p, i) => (
            <motion.button
              key={p.id}
              type="button"
              onClick={() => navigate(`/biens/${p.id}`)}
              data-testid={`biens-card-${p.id}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="group text-left"
            >
              <div className="relative h-64 overflow-hidden rounded-2xl">
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/55 via-transparent to-transparent" />
                <img
                  src="/kemelia-logo-square.png"
                  alt="Kemelia Estate"
                  className="absolute right-3 top-3 h-11 w-11 rounded-lg border border-gold/40 object-cover shadow-[0_4px_14px_rgba(0,0,0,0.35)]"
                />
                {p.tags?.[0] && (
                  <span className="absolute left-4 top-4 rounded-full bg-white/85 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-azur backdrop-blur">
                    {p.tags[0]}
                  </span>
                )}
                <span className="absolute bottom-4 left-4 font-serif text-2xl font-semibold text-white">{p.price}</span>
              </div>
              <div className="mt-4 px-1">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-terracotta">
                  {p.type} — {p.location}
                </span>
                <h3 className="mt-1.5 font-serif text-xl font-semibold leading-snug text-azur transition-colors group-hover:text-terracotta">
                  {p.title}
                </h3>
                <div className="mt-2 flex items-center gap-4 text-sm font-light text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <BedDouble size={15} className="text-gold" /> {p.bedrooms} chambres
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Maximize size={15} className="text-gold" /> {p.surface}
                  </span>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
        {properties.length === 0 && (
          <p className="py-20 text-center font-serif text-2xl italic text-slate-400">
            Aucun bien ne correspond à vos critères — créez une alerte personnalisée depuis l'accueil.
          </p>
        )}
      </section>

      <Dialog open={!!focus} onOpenChange={(open) => !open && setFocus(null)}>
        <DialogContent className="max-w-3xl overflow-hidden p-0" data-testid="property-modal">
          {focus && (
            <div className="grid md:grid-cols-2">
              <div className="flex flex-col bg-slate-950/5">
                <div className="relative h-64 md:h-auto md:min-h-[420px] md:flex-1">
                  <img
                    src={gallery[imgIdx]}
                    alt={focus.title}
                    data-testid="property-gallery-image"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent md:bg-gradient-to-r" />
                  {gallery.length > 1 && (
                    <>
                      <button
                        onClick={() => setImgIdx((imgIdx - 1 + gallery.length) % gallery.length)}
                        data-testid="gallery-prev"
                        aria-label="Photo précédente"
                        className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2 text-azur backdrop-blur transition-colors hover:bg-gold"
                      >
                        <ChevronLeft size={18} />
                      </button>
                      <button
                        onClick={() => setImgIdx((imgIdx + 1) % gallery.length)}
                        data-testid="gallery-next"
                        aria-label="Photo suivante"
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2 text-azur backdrop-blur transition-colors hover:bg-gold"
                      >
                        <ChevronRight size={18} />
                      </button>
                      <span data-testid="gallery-counter" className="absolute bottom-3 right-3 rounded-full bg-slate-950/60 px-3 py-1 font-mono text-[10px] tracking-[0.2em] text-white">
                        {imgIdx + 1} / {gallery.length}
                      </span>
                    </>
                  )}
                </div>
                {gallery.length > 1 && (
                  <div data-testid="gallery-thumbs" className="no-scrollbar flex gap-2 overflow-x-auto p-3">
                    {gallery.map((g, i) => (
                      <button
                        key={g + i}
                        onClick={() => setImgIdx(i)}
                        data-testid={`gallery-thumb-${i}`}
                        className={`h-16 w-20 shrink-0 overflow-hidden rounded-lg border-2 transition-all ${
                          i === imgIdx ? "border-gold" : "border-transparent opacity-60 hover:opacity-100"
                        }`}
                      >
                        <img src={g} alt="" className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="max-h-[85vh] overflow-y-auto p-8">
                <DialogHeader>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-terracotta">
                    {focus.type} — {focus.location}
                  </span>
                  <DialogTitle className="font-serif text-2xl font-semibold text-azur">{focus.title}</DialogTitle>
                </DialogHeader>
                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm font-light text-slate-600">
                  <span className="flex items-center gap-1.5"><BedDouble size={15} className="text-gold" /> {focus.bedrooms} chambres</span>
                  <span className="flex items-center gap-1.5"><Maximize size={15} className="text-gold" /> {focus.surface}</span>
                  <span className="flex items-center gap-1.5"><MapPin size={15} className="text-gold" /> {focus.location}</span>
                </div>
                <div className="mt-4 font-serif text-3xl font-semibold text-azur">{focus.price}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {focus.tags?.map((t) => (
                    <span key={t} className="rounded-full bg-sand px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-azur">
                      {t}
                    </span>
                  ))}
                </div>
                <p className="mt-5 text-sm font-light leading-relaxed text-slate-600">{focus.description}</p>

                <form onSubmit={sendLead} data-testid="property-contact-form" className="mt-7 space-y-3 border-t border-slate-100 pt-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                    Intéressé par ce bien ?
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <input required placeholder="Nom" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} data-testid="property-contact-name" className={selectCls + " w-full"} />
                    <input required type="email" placeholder="Email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} data-testid="property-contact-email" className={selectCls + " w-full"} />
                  </div>
                  <input placeholder="Téléphone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} data-testid="property-contact-phone" className={selectCls + " w-full"} />
                  <textarea placeholder="Votre message" rows={3} value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} data-testid="property-contact-message" className={selectCls + " w-full resize-none"} />
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="property-contact-submit"
                    className="w-full rounded-xl bg-azur px-6 py-3.5 text-sm font-medium text-sand transition-colors hover:bg-terracotta disabled:opacity-60"
                  >
                    {sending ? "Envoi en cours…" : "Être contacté pour ce bien"}
                  </button>
                </form>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
